import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, Bot, Zap, Eye } from 'lucide-react';

const LaunchpadFinalCTA = () => {
  return (
    <section className="relative py-16 sm:py-20 md:py-24 lg:py-28 bg-gradient-to-br from-[#003a70] via-[#002a60] to-[#001a50] overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-[10%] left-[5%] w-32 h-32 bg-[#FEE6D8] rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-[60%] right-[10%] w-40 h-40 bg-[#FEE6D8] rounded-full blur-2xl animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute bottom-[20%] left-[15%] w-24 h-24 bg-[#FEE6D8] rounded-full blur-3xl animate-pulse" style={{animationDelay: '4s'}}></div>
      </div>
      
      {/* Floating icons */}
      <div className="absolute inset-0 pointer-events-none">
        <Bot className="absolute top-[15%] left-[8%] w-6 h-6 text-[#FEE6D8]/30 animate-pulse" />
        <Zap className="absolute top-[25%] right-[12%] w-8 h-8 text-[#FEE6D8]/25 animate-pulse" style={{animationDelay: '1s'}} />
        <Eye className="absolute bottom-[30%] right-[8%] w-5 h-5 text-[#FEE6D8]/35 animate-pulse" style={{animationDelay: '3s'}} />
        <Bot className="absolute bottom-[15%] left-[12%] w-7 h-7 text-[#FEE6D8]/20 animate-pulse" style={{animationDelay: '2s'}} />
      </div>

      <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6">
        {/* Main heading with gradient text */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-[0.9] tracking-tight mb-4">
            <span className="block bg-gradient-to-r from-white via-[#FEE6D8] to-white bg-clip-text text-transparent">
              DON'T WAIT
            </span>
            <span className="block text-white drop-shadow-lg">
              ANOTHER DAY!
            </span>
          </h2>
          
          <div className="w-32 h-1 bg-gradient-to-r from-transparent via-[#FEE6D8] to-transparent mx-auto mb-6"></div>
          
          <p className="text-xl sm:text-2xl md:text-3xl text-[#FEE6D8]/90 font-medium max-w-3xl mx-auto leading-relaxed">
            Try AskSpot Unlimited Risk-Free for 60 Days &
            <span className="block font-bold text-white mt-2">Lock In HUGE Savings!</span>
          </p>
        </div>

        {/* Value propositions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20">
            <div className="text-2xl mb-2">🤖</div>
            <div className="text-[#FEE6D8] font-bold text-sm sm:text-base">Unlimited AI Assistants</div>
            <div className="text-white/80 text-xs sm:text-sm">Build as many as you want</div>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20">
            <div className="text-2xl mb-2">⚡</div>
            <div className="text-[#FEE6D8] font-bold text-sm sm:text-base">All Features Included</div>
            <div className="text-white/80 text-xs sm:text-sm">No limitations, full access</div>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20">
            <div className="text-2xl mb-2">👁️</div>
            <div className="text-[#FEE6D8] font-bold text-sm sm:text-base">See the Power</div>
            <div className="text-white/80 text-xs sm:text-sm">Experience it firsthand</div>
          </div>
        </div>

        {/* Pricing highlight */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/20 mb-8 sm:mb-10">
          <div className="text-center">
            <div className="mb-4">
              <span className="text-2xl sm:text-3xl text-white font-medium">Start with </span> 
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-[#FEE6D8] to-white bg-clip-text text-transparent font-black text-4xl sm:text-5xl">
                  ZERO COST
                </span>
                <div className="absolute -bottom-1 left-0 right-0 h-1 bg-gradient-to-r from-[#FEE6D8] to-white/30 rounded-full"></div>
              </span>
              <span className="block text-xl sm:text-2xl text-white/80 mt-2">for Two Full Months</span>
            </div>
            
            <div className="border-t border-white/10 pt-4">
              <span className="text-xl sm:text-2xl text-white">Then continue for just </span> 
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-[#FEE6D8] to-white bg-clip-text text-transparent font-bold text-3xl sm:text-4xl">
                  $59/mo
                </span>
                <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-[#FEE6D8] to-white/30 rounded-full"></div>
              </span>
              <span className="text-white/60 line-through ml-3 text-xl sm:text-2xl">(Normally $99/mo!)</span>
            </div>
          </div>
        </div>

        {/* Urgency text */}
        <div className="mb-8 sm:mb-10">
          <p className="text-lg sm:text-xl text-white/90 mb-4">
            The AI revolution is happening NOW.
          </p>
          <p className="text-base sm:text-lg text-[#FEE6D8]/80">
            Don't miss out on testing AskSpot risk-free while this special offer lasts.
          </p>
        </div>

        {/* CTA Button */}
        <div className="flex justify-center mb-4">
          <Button 
            size="lg" 
            className="bg-gradient-to-r from-[#FEE6D8] to-[#FEE6D8]/90 hover:from-[#FEE6D8]/90 hover:to-[#FEE6D8]/80 text-[#003a70] text-xl sm:text-2xl md:text-3xl font-black py-6 sm:py-8 md:py-10 px-8 sm:px-12 md:px-16 shadow-2xl hover:shadow-[#FEE6D8]/20 group transform hover:scale-105 transition-all duration-300 rounded-2xl border-4 border-[#FEE6D8]/30 min-h-[70px] sm:min-h-[80px] md:min-h-[90px] relative overflow-hidden"
            onClick={() => window.open('https://buy.stripe.com/bIY17EaCh2FI0NicN5', '_blank')}
          >
            {/* Button shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
            
            <span className="relative flex items-center justify-center gap-4">
              🔥 CLAIM YOUR 60-DAY TRIAL
              <ArrowRight className="h-8 w-8 sm:h-10 sm:w-10 group-hover:translate-x-2 transition-transform flex-shrink-0" />
            </span>
          </Button>
        </div>

        {/* Warning Text */}
        <div className="mb-6 sm:mb-8">
          <p className="text-lg sm:text-xl text-[#FEE6D8] font-medium">
            ⚠️ This Offer Is For New Users Only
          </p>
        </div>

        {/* Final urgency line */}
        <div>
          <p className="text-sm sm:text-base text-white/70 italic">
            Join thousands using AskSpot to build unlimited AI assistants
          </p>
        </div>
      </div>
    </section>
  );
};

export default LaunchpadFinalCTA;
